/**
 * Waybill PDF Processor
 * Processes waybill PDFs to extract and reformat for thermal printing
 */

import jsPDF from "jspdf";
import {
  renderPageToCanvas,
  cropCanvasRegion,
  loadPdfFromBlob,
  getPdfPageCount,
  calculateWaybillCropRegion,
  calculateScaledDimensions,
  cleanupCanvases
} from "./pdfUtil";

/**
 * Main function to process waybill PDF
 * @param {File} pdfFile - PDF file to process
 * @param {Object} options - Processing options
 * @returns {Promise<Blob>} Processed PDF blob
 */
export async function processWaybillPdf(pdfFile, options = {}) {
  const {
    cropOptions = {},
    pageFormat = [4, 6], // inches
    scale = 2,
    onProgress = null
  } = options;

  const canvases = []; // Track canvases for cleanup

  try {
    // Load PDF
    const pdfDoc = await loadPdfFromBlob(pdfFile);
    const pageCount = getPdfPageCount(pdfDoc);

    // Initialize output PDF with thermal label dimensions
    const pdf = new jsPDF({
      orientation: "portrait",
      unit: "in",
      format: pageFormat,
      compress: true
    });

    let isFirstPage = true;

    // Process each page
    for (let pageNum = 1; pageNum <= pageCount; pageNum++) {
      try {
        const page = await pdfDoc.getPage(pageNum);

        // Render page to canvas
        const canvas = await renderPageToCanvas(page, scale);
        canvases.push(canvas);

        // Calculate crop region for waybill content by trimming whitespace from the rendered page.
        const cropRegion = calculateWaybillCropRegion(canvas, cropOptions);

        // Crop the region
        const croppedImageData = cropCanvasRegion(canvas, cropRegion);

        // Calculate scaled dimensions to fit thermal label
        const scaledDims = calculateScaledDimensions(
          cropRegion.width,
          cropRegion.height,
          pageFormat[0],
          pageFormat[1]
        );

        // Add to PDF
        if (!isFirstPage) {
          pdf.addPage(pageFormat, "portrait");
        }

        pdf.addImage(
          croppedImageData,
          "PNG",
          scaledDims.x,
          scaledDims.y,
          scaledDims.width,
          scaledDims.height
        );

        isFirstPage = false;

        // Report progress
        if (onProgress) {
          onProgress({
            current: pageNum,
            total: pageCount,
            percentage: Math.round((pageNum / pageCount) * 100)
          });
        }
      } catch (pageError) {
        console.error(`Error processing page ${pageNum}:`, pageError);
        // Continue with next page instead of failing entirely
        if (onProgress) {
          onProgress({
            current: pageNum,
            total: pageCount,
            percentage: Math.round((pageNum / pageCount) * 100),
            error: `Page ${pageNum} failed: ${pageError.message}`
          });
        }
      }
    }

    // Generate PDF blob
    const pdfBlob = pdf.output("blob");

    return pdfBlob;
  } finally {
    // Cleanup canvases to prevent memory leaks
    cleanupCanvases(canvases);
  }
}

/**
 * Convert processed PDF blob back to File object
 * @param {Blob} pdfBlob - PDF blob
 * @param {string} fileName - Original file name
 * @returns {File} File object
 */
export function blobToFile(pdfBlob, fileName) {
  // Generate new filename with _processed suffix
  const nameParts = fileName.split(".");
  const baseName = nameParts.slice(0, -1).join(".");
  const extension = nameParts[nameParts.length - 1];
  const newFileName = `${baseName}_processed.${extension}`;

  console.log(`Converting blob to file: ${newFileName}`);
  return new File([pdfBlob], newFileName, { type: "application/pdf" });
}

/**
 * Validate that file is a PDF
 * @param {File} file - File to validate
 * @returns {boolean} True if valid PDF
 */
export function isPdfFile(file) {
  return (
    file &&
    (file.type === "application/pdf" ||
      file.name.toLowerCase().endsWith(".pdf"))
  );
}

/**
 * Validate PDF file size (max 50MB by default)
 * @param {File} file - File to validate
 * @param {number} maxSizeMB - Max file size in MB
 * @returns {boolean} True if within size limit
 */
export function validatePdfSize(file, maxSizeMB = 50) {
  const maxBytes = maxSizeMB * 1024 * 1024;
  return file.size <= maxBytes;
}
